import Echo from "laravel-echo";
import Pusher from "pusher-js";

import { ReactNode, createContext, useEffect, useState } from "react";

import { selectAuth } from "store/auth/selectors";
import { useAppSelector } from "store/hooks";

import { SocketStatuses } from "../../types/socket.types";

type SocketContextProps = {
  stateEcho: Echo | undefined;
  socketConnected: boolean;
  socketUnavailable: boolean;
  socketFailed: boolean;
};

type SocketProviderProps = {
  children: ReactNode | ReactNode[];
};

export const SocketContext = createContext<SocketContextProps>({
  stateEcho: undefined,
  socketConnected: false,
  socketUnavailable: false,
  socketFailed: false,
});

const SocketProvider = ({ children }: SocketProviderProps) => {
  const [stateEcho, setStateEcho] = useState<Echo | undefined>(undefined);
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketUnavailable, setSocketUnavailable] = useState(false);
  const [socketFailed, setSocketFailed] = useState(false);
  const auth = useAppSelector(selectAuth);

  const socketStatuses = (_stateEcho: Echo | undefined) => {
    if (_stateEcho?.connector) {
      _stateEcho.connector.pusher.connection.bind(
        SocketStatuses.CONNECTED,
        () => {
          console.log("Socket CONNECTED");
          setSocketConnected(true);
        }
      );
      _stateEcho.connector.pusher.connection.bind(
        SocketStatuses.DISCONNECTED,
        () => {
          console.log("Socket DISCONNECTED");
          setSocketConnected(false);
        }
      );
      _stateEcho.connector.pusher.connection.bind(
        SocketStatuses.CONNECTING,
        () => {
          console.log("Socket CONNECTING");
          setSocketUnavailable(true);
        }
      );
      _stateEcho.connector.pusher.connection.bind(SocketStatuses.FAILED, () => {
        console.log("Socket FAILED");
        setSocketFailed(false);
      });
    }
  };

  const createEcho = (token: string) => {
    const options = {
      broadcaster: "pusher",
      key: process.env.REACT_APP_OMANA_WEBSOCKET_KEY || "",
      wsHost: process.env.REACT_APP_OMANA_WEBSOCKET_HOST,
      wsPort: 443,
      wssPort: 443,
      authEndpoint: process.env.REACT_APP_OMANA_WEBSOCKET_AUTH_ENDPOINT,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json, text/plain, */*",
        },
      },
    };
    return new Echo({
      ...options,
      client: new Pusher(options.key, {
        ...options,
      }),
    });
  };

  useEffect(() => {
    if (auth.token) {
      const createdEcho = createEcho(auth.token);
      setStateEcho(createdEcho);
      socketStatuses(createdEcho);
      return () => {
        createdEcho?.disconnect();
        setStateEcho(undefined);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token]);

  return (
    <SocketContext.Provider
      value={{
        stateEcho,
        socketConnected,
        socketUnavailable,
        socketFailed,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
